import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ShieldCheck, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import SEO from '@/components/SEO';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const email = searchParams.get('email') || '';
  const tokenFromUrl = searchParams.get('token') || '';
  const loginPath = '/login';

  const [formData, setFormData] = useState({
    otp: tokenFromUrl,
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          otp: formData.otp,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        navigate(loginPath);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-20 relative overflow-hidden transition-all duration-1000">
      <SEO title="Reset Password" noindex />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(0,0,0,0.02)_0%,transparent_50%)]" />
      </div>

      <div className="w-full max-w-[480px] relative z-10 transition-all duration-700">
        <div className="text-center mb-10">
          <div className="w-24 h-24 bg-black rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-black/20">
            <KeyRound className="text-white w-12 h-12" />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold font-display text-black uppercase tracking-tighter mb-3">New Password</h1>
          <p className="text-zinc-600 font-medium italic text-sm">Reset password for <span className="text-black font-bold not-italic">{email}</span></p>
        </div>

        <div className="bg-white p-10 rounded-[3.5rem] border border-black/5 shadow-2xl shadow-black/5">
          <form onSubmit={handleSubmit} className="space-y-10">
            {!tokenFromUrl && (
              <div className="space-y-4 text-center">
                <label className="text-[10px] font-black uppercase tracking-[0.4em] text-zinc-600 block">Verification Code</label>
                <input
                  type="text"
                  required
                  maxLength={6}
                  value={formData.otp}
                  onChange={(e) => setFormData({...formData, otp: e.target.value.replace(/\D/g, '')})}
                  className="w-full h-24 bg-zinc-50 border border-black/5 rounded-[2.5rem] text-black text-center text-5xl tracking-[0.3em] font-black focus:border-black focus:ring-8 focus:ring-black/5 outline-none transition-all shadow-xl shadow-black/5"
                  placeholder="000000"
                />
              </div>
            )}

            {tokenFromUrl && (
              <div className="bg-zinc-50 p-6 rounded-2xl border border-black/5 text-center">
                <Badge className="bg-green-100 text-green-700 border-none mb-2">Verified Link</Badge>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Your identity has been verified via the secure link sent to your email.</p>
              </div>
            )}

            <div className="space-y-6">
               <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 ml-1">New Password</label>
                <div className="relative group/input">
                  <Lock className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-black" />
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full pl-14 pr-6 py-5 bg-zinc-50/50 border border-black/5 rounded-2xl text-black font-medium placeholder:text-zinc-400 focus:ring-4 focus:ring-black/5 focus:border-black outline-none transition-all"
                    placeholder="********"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 ml-1">Confirm Password</label>
                <div className="relative group/input">
                  <Lock className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-black" />
                  <input
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="w-full pl-14 pr-6 py-5 bg-zinc-50/50 border border-black/5 rounded-2xl text-black font-medium placeholder:text-zinc-400 focus:ring-4 focus:ring-black/5 focus:border-black outline-none transition-all"
                    placeholder="********"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-[10px] font-black uppercase tracking-widest text-center animate-in fade-in slide-in-from-top-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (!tokenFromUrl && formData.otp.length !== 6)}
              className="w-full h-18 bg-black text-white hover:bg-zinc-800 font-black uppercase tracking-[0.2em] rounded-2xl shadow-2xl shadow-black/20 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? 'Resetting...' : (
                <>
                  Update Password <ShieldCheck className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-10 text-center">
             <button onClick={() => navigate('/login')} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 hover:text-black transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
